import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, baixarBlob, mensagemDoErro } from '../api';
import { useSessao } from '../sessao';
import { DocumentosEmitidos } from './DocumentosEmitidos';
import { NomeDaAmostra, daApi, nomeDaAmostra } from '../NomeDaAmostra';
import {
  AcaoBloqueada, Aviso, Campo, Carregando, ErroDaApi, Escolha, Selo, Vazio, abrirProtegido,
} from '../ui';
import {
  caminhoDoCaderno, caminhoDoQuadro, verPanorama, verPlanoDoCaderno, verQuadro,
  type EscolhaDoCaderno, type FormatoQuadro, type Panorama, type PlanoDoCaderno,
  type Quadro, type TipoCaderno,
} from '../api/relatorios';

/**
 * Relatórios — o caderno de laudos e o quadro resumo (onda 214).
 *
 * «Crie o módulo de emissão de relatórios. Poderá ser emitido relatório
 *  compilado por amostra, compilado por ensaio (por exemplo, todos os
 *  adensamentos de várias amostras), compilado total (todos os ensaios de
 *  todas as amostras). Exportação de tabela resumo (compilado na
 *  horizontal ou na vertical), ou padrão SANEPAR.»
 *                                              — Humberto, 28/09/2026
 *
 * ── Por que a relação aparece ANTES do botão ────────────────────────
 *
 * `CA-28`: o lote nunca sai parecendo completo quando não é. A tela diz,
 * antes de gerar, quais laudos entram e quais ficam de fora e por quê — o
 * mesmo quadro que a relação dos laudos (a folha 2 do PDF) imprime. Quem
 * gera não descobre no cliente que faltava o adensamento.
 *
 * ── E por que o quadro tem prévia ───────────────────────────────────
 *
 * Porque a planilha é o que vai para o cliente e ele confere a medição
 * por ela. Ver os «▲» e «◐» na tela antes de baixar é o que evita mandar
 * um quadro com metade das células esperando aprovação.
 */

interface OsAchada {
  id: string; numero: string; cliente: string | null; obra_nome: string | null;
}

const TIPOS: { valor: TipoCaderno; texto: string; detalhe: string }[] = [
  { valor: 'amostra', texto: 'Por amostra', detalhe: 'todos os ensaios de uma amostra' },
  { valor: 'ensaio', texto: 'Por ensaio', detalhe: 'o mesmo ensaio em várias amostras' },
  { valor: 'total', texto: 'Total da OS', detalhe: 'todos os ensaios de todas as amostras' },
];

const FORMATOS: { valor: FormatoQuadro; texto: string; detalhe: string }[] = [
  { valor: 'vertical', texto: 'Vertical', detalhe: 'parâmetro por linha, amostra por coluna' },
  { valor: 'horizontal', texto: 'Horizontal', detalhe: 'amostra por linha, parâmetro por coluna' },
  { valor: 'sanepar', texto: 'Padrão SANEPAR', detalhe: 'o QUADRO RESUMO DE ENSAIOS' },
];

const revisao = (r: number | null) => `R${String(r ?? 0).padStart(2, '0')}`;

function celulaTexto(c: { num?: number; texto?: string; casas?: number } | undefined): string {
  if (!c) return '—';
  if (c.num !== undefined) {
    const casas = c.casas ?? 2;
    return c.num.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
  }
  return c.texto ?? '—';
}

/** A escolha da OS — a busca é a mesma da lista de OS. */
function EscolherOs({ aoEscolher }: { aoEscolher: (id: string) => void }) {
  const [busca, setBusca] = useState('');
  const [achadas, setAchadas] = useState<OsAchada[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  useEffect(() => {
    const termo = busca.trim();
    if (termo.length < 2) { setAchadas(null); return undefined; }
    const t = setTimeout(() => {
      api<OsAchada[]>(`/os?busca=${encodeURIComponent(termo)}&arquivadas=incluir`)
        .then((r) => { setAchadas(r.slice(0, 12)); setErro(null); })
        .catch(setErro);
    }, 250);
    return () => clearTimeout(t);
  }, [busca]);

  return (
    <section className="cartao">
      <Campo rotulo="Ordem de serviço" placeholder="Número da OS, obra ou cliente"
        value={busca} onChange={(e) => setBusca(e.target.value)} autoFocus
        dica="Escolha a OS cujos laudos e resultados você quer reunir" />
      <ErroDaApi erro={erro} />
      {achadas && achadas.length === 0 && <Vazio>Nenhuma OS encontrada para «{busca}».</Vazio>}
      {achadas && achadas.length > 0 && (
        <ul className="lista-escolha" data-teste="os-achadas">
          {achadas.map((o) => (
            <li key={o.id}>
              <button type="button" className="como-link" onClick={() => aoEscolher(o.id)}>
                <b>{o.numero}</b> · {o.cliente ?? '—'} · {o.obra_nome ?? '—'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function Relatorios() {
  const { pode } = useSessao();
  const [params, setParams] = useSearchParams();
  const osId = params.get('os');
  const [pan, setPan] = useState<Panorama | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  const [tipo, setTipo] = useState<TipoCaderno>('total');
  const [amostra, setAmostra] = useState<string | null>(null);
  const [metodo, setMetodo] = useState<string | null>(null);
  const [preliminares, setPreliminares] = useState(false);
  const [plano, setPlano] = useState<PlanoDoCaderno | null>(null);
  const [erroPlano, setErroPlano] = useState<unknown>(null);
  const [gerando, setGerando] = useState(false);
  const [recado, setRecado] = useState<string | null>(null);

  const [quadro, setQuadro] = useState<Quadro | null>(null);
  const [baixando, setBaixando] = useState<FormatoQuadro | null>(null);
  const [erroQuadro, setErroQuadro] = useState<unknown>(null);

  const podeGerar = pode('relatorio.exportar_lote');

  useEffect(() => {
    setPan(null); setQuadro(null); setPlano(null); setErro(null);
    setAmostra(null); setMetodo(null);
    if (!osId) return;
    verPanorama(osId).then(setPan).catch(setErro);
    verQuadro(osId).then(setQuadro).catch(setErroQuadro);
  }, [osId]);

  const escolha: EscolhaDoCaderno = useMemo(
    () => ({ tipo, amostra, metodo, preliminares }), [tipo, amostra, metodo, preliminares]);
  const faltaEscolha = (tipo === 'amostra' && !amostra) || (tipo === 'ensaio' && !metodo);

  const carregarPlano = useCallback(async () => {
    if (!osId || faltaEscolha) { setPlano(null); return; }
    try {
      setErroPlano(null);
      setPlano(await verPlanoDoCaderno(osId, escolha));
    } catch (e) { setErroPlano(e); }
  }, [osId, escolha, faltaEscolha]);
  useEffect(() => { void carregarPlano(); }, [carregarPlano]);

  const gerar = async () => {
    if (!osId) return;
    setGerando(true); setRecado(null);
    try {
      await abrirProtegido(caminhoDoCaderno(osId, escolha),
        `caderno-${tipo}-OS${pan?.os.numero ?? ''}.pdf`);
      setRecado('Caderno gerado — abriu em outra aba. Os laudos emitidos que saíram nele '
        + 'ficaram registrados como baixados por você.');
    } catch (e) { setRecado(mensagemDoErro(e)); }
    finally { setGerando(false); }
  };

  const baixarQuadro = async (f: FormatoQuadro) => {
    if (!osId) return;
    setBaixando(f); setErroQuadro(null);
    try {
      const blob = await baixarBlob(caminhoDoQuadro(osId, f));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quadro-resumo-${f}-OS${pan?.os.numero ?? ''}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { setErroQuadro(e); }
    finally { setBaixando(null); }
  };

  const aba = params.get('aba');
  const trocarAba = (a: string | null) => {
    const p = new URLSearchParams(params);
    if (a) p.set('aba', a); else p.delete('aba');
    setParams(p, { replace: true });
  };

  const trocarOs = (id: string | null) => {
    const p = new URLSearchParams(params);
    if (id) p.set('os', id); else p.delete('os');
    setParams(p, { replace: false });
  };

  return (
    <div className="tela-relatorios">
      <div className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Relatórios</h1>
          <p className="sub">
            Os documentos emitidos — o mais recente primeiro — e, por ordem de
            serviço, o caderno de laudos e o quadro resumo em Excel
          </p>
        </div>
        {osId && (
          <button type="button" className="botao secundario" onClick={() => trocarOs(null)}>
            Trocar de OS
          </button>
        )}
      </div>

      {/*
        * Duas abas (onda 272): a lista dos documentos emitidos, que é o que
        * se procura todo dia, e o caderno de uma OS, que é o que se monta
        * para entregar. Com uma OS escolhida, a tela é o caderno dela.
        */}
      {!osId && (
        <div className="abas-lista" role="tablist" aria-label="Relatórios">
          <button type="button" role="tab" aria-selected={aba !== 'caderno'}
            className={aba !== 'caderno' ? 'aqui' : ''} onClick={() => trocarAba(null)}>
            Documentos emitidos
          </button>
          <button type="button" role="tab" aria-selected={aba === 'caderno'}
            className={aba === 'caderno' ? 'aqui' : ''} onClick={() => trocarAba('caderno')}>
            Caderno de uma OS
          </button>
        </div>
      )}
      {!osId && aba !== 'caderno' && <DocumentosEmitidos />}
      {!osId && aba === 'caderno' && <EscolherOs aoEscolher={(id) => trocarOs(id)} />}
      {osId && erro != null && <ErroDaApi erro={erro} />}
      {osId && !erro && !pan && <Carregando texto="Reunindo os ensaios da OS" />}

      {pan && (
        <>
          <section className="cartao cabeca-os" data-teste="os-do-relatorio">
            <strong>OS {pan.os.numero}</strong>
            <span>{pan.os.cliente ?? '—'}</span>
            <span>{[pan.os.obra_codigo, pan.os.obra_nome].filter(Boolean).join(' — ')}</span>
            <span className="det">
              {pan.amostras.length} amostra(s) · {pan.metodos.reduce((n, m) => n + m.ensaios, 0)} ensaio(s)
              · {pan.metodos.reduce((n, m) => n + m.aprovados, 0)} aprovado(s)
              · {pan.metodos.reduce((n, m) => n + m.emitidos, 0)} laudo(s) emitido(s)
            </span>
          </section>

          {/* ── O caderno de laudos ─────────────────────────────── */}
          <section className="cartao" data-teste="caderno">
            <h2>Caderno de laudos (PDF)</h2>
            <Escolha rotulo="Compilado" limpavel={false}
              opcoes={TIPOS.map((t) => ({ valor: t.valor, texto: t.texto, detalhe: t.detalhe }))}
              valor={tipo} aoEscolher={(v) => v && setTipo(v as TipoCaderno)} />

            {tipo === 'amostra' && (
              <Escolha rotulo="Amostra" classe="escolha-longa"
                opcoes={pan.amostras.map((a) => ({
                  // 303 (RN-248): a amostra pelo nome do cliente, com o código.
                  valor: a.id, texto: nomeDaAmostra(daApi(a)),
                  detalhe: `${a.ensaios} ensaio(s), ${a.emitidos} emitido(s)`,
                }))}
                valor={amostra} aoEscolher={setAmostra} />
            )}
            {tipo === 'ensaio' && (
              <Escolha rotulo="Ensaio" classe="escolha-longa"
                opcoes={pan.metodos.map((m) => ({
                  valor: m.id, texto: m.codigo,
                  detalhe: `${m.nome} · ${m.amostras} amostra(s), ${m.emitidos} emitido(s)`,
                }))}
                valor={metodo} aoEscolher={setMetodo} />
            )}

            <label className="campo marca">
              <input type="checkbox" checked={preliminares}
                onChange={(e) => setPreliminares(e.target.checked)} />
              Incluir os laudos PRELIMINARES (ensaios aprovados cujo laudo ainda não foi emitido)
            </label>

            {faltaEscolha && <Vazio>Escolha {tipo === 'amostra' ? 'a amostra' : 'o ensaio'} para ver
              o que entra no caderno.</Vazio>}
            <ErroDaApi erro={erroPlano} />

            {plano && !faltaEscolha && (
              <div className="plano-caderno">
                <h3>Entram no caderno ({plano.entram.length})</h3>
                {plano.entram.length === 0
                  ? <Vazio>Nenhum laudo neste recorte ainda.</Vazio>
                  : (
                    <table className="dados" data-teste="entram">
                      <thead><tr>
                        <th>Amostra</th><th>Ensaio</th><th>Descrição</th><th>Laudo</th>
                      </tr></thead>
                      <tbody>
                        {plano.entram.map((i) => (
                          <tr key={i.ensaioId}>
                            <td><NomeDaAmostra amostra={{
                              codigo: i.amostraCodigo, identificacao: i.identificacao }} /></td>
                            <td><b>{i.metodoCodigo}</b></td>
                            <td>{i.metodoNome}</td>
                            <td>{i.situacao === 'preliminar'
                              ? <Selo tom="atencao">PRELIMINAR</Selo>
                              : i.mesmoLaudoDe ? 'no mesmo laudo'
                                : `${i.numero} · ${revisao(i.revisao)}`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                <h3>Ficam de fora ({plano.fora.length})</h3>
                {plano.fora.length === 0
                  ? <p className="det">Nenhum ensaio do recorte fica de fora.</p>
                  : (
                    <table className="dados" data-teste="fora">
                      <thead><tr><th>Amostra</th><th>Ensaio</th><th>Por quê</th></tr></thead>
                      <tbody>
                        {plano.fora.map((i) => (
                          <tr key={i.ensaioId}>
                            <td><NomeDaAmostra amostra={{
                              codigo: i.amostraCodigo, identificacao: i.identificacao }} /></td>
                            <td><b>{i.metodoCodigo}</b></td>
                            <td>{i.motivo}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                <p className="det">A relação na folha 2 do PDF, logo depois da capa, repete
                  estas duas listas. Laudo emitido entra com os bytes do acervo, conferidos
                  pelo hash da emissão.</p>
              </div>
            )}

            <div className="acoes">
              {podeGerar ? (
                <button type="button" className="botao" data-teste="gerar-caderno"
                  disabled={gerando || faltaEscolha || !plano || plano.entram.length === 0}
                  onClick={() => void gerar()}>
                  {gerando ? 'Gerando o caderno…' : 'Gerar o caderno em PDF'}
                </button>
              ) : (
                <AcaoBloqueada texto="Gerar o caderno em PDF" oQueFaz="não exporta relatórios em lote"
                  permissao="Exportar relatórios em lote" />
              )}
            </div>
            {recado && <Aviso tipo="info">{recado}</Aviso>}
          </section>

          {/* ── O quadro resumo ─────────────────────────────────── */}
          <section className="cartao" data-teste="quadro">
            <h2>Quadro resumo (Excel)</h2>
            <p className="det">Só números de ensaios aprovados. Os códigos de ensaio são os do
              catálogo da casa.</p>
            <div className="acoes">
              {FORMATOS.map((f) => (podeGerar ? (
                <button key={f.valor} type="button" className="botao secundario"
                  data-teste={`quadro-${f.valor}`} title={f.detalhe}
                  disabled={baixando !== null} onClick={() => void baixarQuadro(f.valor)}>
                  {baixando === f.valor ? 'Gerando…' : `${f.texto} (.xlsx)`}
                </button>
              ) : null))}
              {!podeGerar && (
                <AcaoBloqueada texto="Baixar o quadro resumo" oQueFaz="não exporta relatórios em lote"
                  permissao="Exportar relatórios em lote" />
              )}
            </div>
            <ErroDaApi erro={erroQuadro} />
            {!quadro && !erroQuadro && <Carregando texto="Montando o quadro" />}
            {quadro && quadro.linhas.length === 0 && (
              <Vazio>Nenhum ensaio nesta OS ainda — o quadro sai só com a identificação das amostras.</Vazio>
            )}
            {quadro && quadro.linhas.length > 0 && (
              <div className="rolar-x">
                <table className="dados quadro-resumo" data-teste="previa-quadro">
                  <thead>
                    <tr>
                      <th>Ensaio</th><th>Parâmetro</th><th>Unid.</th>
                      {/* 303 (RN-248): a coluna é a amostra pelo nome do cliente. */}
                      {quadro.amostras.map((a) => (
                        <th key={a.id} title={nomeDaAmostra(a)}><NomeDaAmostra amostra={a} /></th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {quadro.linhas.map((l) => (
                      <tr key={`${l.metodo}|${l.parametro}`}>
                        <td><b>{l.metodo}</b></td>
                        <td>{l.rotulo}</td>
                        <td>{l.unidade ?? ''}</td>
                        {quadro.amostras.map((a) => (
                          <td key={a.id} className="num">{celulaTexto(l.celulas[a.id])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {quadro && (
              <ul className="legenda-quadro det">
                {quadro.legenda.map((t) => <li key={t}>{t}</li>)}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
